/**
 * Shared prose styling for rendered document body content.
 *
 * `@tailwindcss/typography` is not installed in this project (no
 * `tailwind.config.*`, no `@plugin "@tailwindcss/typography"` in `index.css`),
 * so a `prose`/`prose-*` class emits no CSS — it is inert. This module is the
 * single source of truth for how headings/paragraphs/lists look, consumed by
 * BOTH the read-only split-view renderer (`Markdown.tsx`) and the rich
 * (TipTap) editor (`documents/tiptapMarkdown.ts`), so the two views cannot
 * silently diverge again. Do not add `prose`/`prose-*` classes anywhere;
 * style new block kinds here instead.
 *
 * Tailwind's preflight (pulled in by `@import "tailwindcss"` in `index.css`)
 * doesn't just leave headings/lists unstyled — it actively RESETS them: a
 * universal `margin:0;padding:0` and `ol,ul,menu{list-style:none}`. Without
 * `@tailwindcss/typography` there is nothing to undo that, so an unstyled
 * `<ul>`/`<ol>` renders with no bullets/numbers at all (not merely
 * differently-sized ones). `LIST_CLASSES` restores markers explicitly via
 * `list-disc`/`list-decimal`, and uses `pl-*` (not `ml-*`) so the list's own
 * padding — zeroed by preflight — is restored to make room for the marker,
 * matching how `@tailwindcss/typography` itself reserves that space.
 */

/**
 * Tailwind classes per heading level — a widened visual hierarchy (BLA-320)
 * so every level is distinct and no heading is at or below body (14px)
 * size: document title 24px / h1 20px / h2 16px / h3 13px (uppercase
 * label). ADR-058 (2026-09-17) had compressed this scale to fix a different
 * collision (body `#` matching the page title's old `text-lg`), but the
 * compression produced a hierarchy inversion — `h3` (12px) rendered SMALLER
 * than body (14px) — and left `h1`/`h2` only 2px apart, both bold. This
 * scale removes both defects: `h1` is deliberately sized BELOW the
 * surrounding page/document title (`DocumentEditor`'s `text-2xl`) via
 * `font-prata`'s serif + `text-xl`, not matched to it.
 *
 * `h1`/the page title carry the self-hosted Prata serif (`--font-prata`,
 * `index.css`) — restricted to these two DISPLAY levels only (>=20px):
 * Prata ships weight 400 only, and a high-contrast serif's hairlines fail
 * at small sizes, so `h2`/`h3`/body stay on the sans stack. `font-normal`
 * is stated explicitly on the Prata elements so the browser cannot
 * synthesize a faux-bold face; hierarchy on those two levels comes from
 * SIZE, not weight. `h2`/`h3` keep real sans bold/semibold weights (the
 * system-ui stack ships genuine 600/700 glyphs, unlike a single-file
 * webfont) — that's what carries their step of the hierarchy.
 */
export const HEADING_STYLES: Record<"h1" | "h2" | "h3", string> = {
  h1: "font-prata font-normal text-xl text-slate-900 mt-4 mb-2 first:mt-0 border-b border-slate-200 pb-1",
  h2: "text-base font-bold text-slate-800 mt-4 mb-1.5 first:mt-0",
  h3: "text-[13px] font-semibold uppercase tracking-wide text-slate-600 mt-3 mb-1 first:mt-0",
};

/** Tailwind classes for a body paragraph. */
export const PARAGRAPH_CLASS = "my-1.5 leading-relaxed";

/** Tailwind classes for a bold headline header paragraph (BLA-516). */
export const HEADLINE_PARAGRAPH_CLASS =
  "mt-4 mb-1.5 leading-relaxed first:mt-0";

/** Tailwind classes per list kind. */
export const LIST_CLASSES: Record<"ul" | "ol", string> = {
  ul: "list-disc pl-5 my-2 space-y-1 [&_p]:my-0 leading-relaxed",
  ol: "list-decimal pl-5 my-2 space-y-1 [&_p]:my-0 leading-relaxed",
};

/**
 * Tailwind classes for a GitHub-style pipe table (BLA-349, BLA-517).
 *
 * Deliberately carries NO alignment utility: every cell gets exactly one from
 * `TABLE_ALIGN_CLASSES` instead. Tailwind's alignment utilities are all
 * single-class selectors in one `@layer utilities` block, so two of them on
 * the same element are resolved by their order in the COMPILED stylesheet, not
 * by `className` order — `.text-left` is emitted after `.text-center`, so a
 * `text-left` baked in here would silently beat the `:---:` column's appended
 * `text-center` and left-align a header the PDF export centres.
 */
export const TABLE_CLASSES = {
  wrapper:
    "rounded-lg border border-slate-200 overflow-x-auto my-3 shadow-xs bg-white",
  table: "w-full border-collapse text-sm",
  th: "bg-slate-50 border-b border-r border-slate-200 last:border-r-0 px-3 py-2 font-semibold text-slate-700",
  td: "border-b border-r border-slate-100 last:border-r-0 px-3 py-2 align-top text-slate-800",
};

/**
 * Tailwind text-alignment class per table column.
 *
 * `left` is explicit (not "") so it also overrides the browser's own
 * `th { text-align: center }` default, mirroring the PDF stylesheet's
 * `th, td { text-align: left }` base.
 */
export const TABLE_ALIGN_CLASSES: Record<"left" | "center" | "right", string> =
  {
    left: "text-left",
    center: "text-center",
    right: "text-right",
  };
